// Portfolio Administration v1.0 PR 3 — raw HTTP transport for the
// Loan-detail API surface (Loans / Setup / Assignments / Parties). Hooks
// in @/hooks/portfolioAdmin wrap these with TanStack Query.

import { api } from "../client";
import type {
  FinancialHistoryRow,
  LoanIntakeBundleDto,
  LoanIntakeProfileDto,
  UpdateLoanIntakeProfileRequest,
} from "@/types/intakeProfile";
import type {
  AddLoanPartyRequest,
  ArchiveVendorRequest,
  AssignLoanRoleRequest,
  BatchIssueRequest,
  BatchIssueResultDto,
  CompleteCalendarItemRequest,
  CompleteSetupItemRequest,
  ComplianceCalendarItemDto,
  CreateLoanProfileFieldDefinitionRequest,
  CreateSetupCatalogItemRequest,
  CreateVendorRequest,
  FileUccAmendmentRequest,
  FileUccContinuationRequest,
  FileUccTerminationRequest,
  GenerateSetupChecklistRequest,
  InsurancePolicyDto,
  ContractComparisonDto,
  IssueVendorInvoiceRequest,
  JustificationRequest,
  LoanAssignmentDto,
  LoanListItemDto,
  LoanPartyAssignmentDto,
  LoanProfileFieldDefinitionDto,
  LoanProfileFieldValueDto,
  LoanSearchParams,
  LoanSetupCatalogItemDto,
  LoanSetupChecklistItemDto,
  LoanSetupChecklistRowDto,
  LoanSummaryDto,
  MoveLoansResultDto,
  MoveLoansToPortfolioRequest,
  PortfolioDashboardDto,
  PortfolioListItemDto,
  CreatePortfolioRequest,
  UpdatePortfolioRequest,
  RecordUccLienSearchRequest,
  RequiredVendorCatalogItemDto,
  SeedPartiesFromDealResultDto,
  SetLoanProfileFieldValueRequest,
  SnoozeCalendarItemRequest,
  UccFilingDto,
  UccLienSearchDto,
  UpdateLoanPartyRequest,
  UpdateLoanProfileFieldDefinitionRequest,
  UpdateLoanTermsRequest,
  UpdateSetupCatalogItemRequest,
  UpdateVendorProfileRequest,
  UpsertInsurancePolicyRequest,
  UpsertLoanFinancialPeriodRequest,
  ConfirmVendorContractTermProposalRequest,
  CreateVendorContractTermRequest,
  DeclineVendorContractTermProposalRequest,
  VendorArchiveImpactDto,
  VendorConnectionDto,
  VendorContractTermDto,
  VendorContractTermProposalDto,
  VendorDto,
  VendorInvoiceDetailDto,
  VendorInvoiceDto,
  VendorInvoiceListParams,
  VfiAgingRowDto,
  VfiScopedSummaryDto,
  VfiPastIncomeRowDto,
  VfiRatingRollupDto,
  VendorLoanAssignmentDto,
  VendorPortalAccountDto,
  VfiDashboardDto,
} from "./types";

// ---- loans -----------------------------------------------------------

export const loansApi = {
  get: (loanId: string) =>
    api.get<LoanSummaryDto>(`/portfolio-admin/loans/${loanId}`),
  // Deal Submission spec §6.1 — intake profile + financial
  // history carried forward from the closed Deal.
  intakeBundle: (loanId: string) =>
    api.get<LoanIntakeBundleDto>(`/portfolio-admin/loans/${loanId}/intake-bundle`),
  search: (params?: LoanSearchParams) =>
    api.get<LoanListItemDto[]>(`/portfolio-admin/loans`, {
      search: params?.search,
      portfolioId: params?.portfolioIds,
      accountState: params?.accountStates,
      assignedB1UserId: params?.assignedB1UserId,
      assignedToMe: params?.assignedToMe ? "true" : undefined,
    }),
  // Portfolios — single + bulk membership move. Reason required whenever a
  // loan leaves an existing portfolio; idempotent on already-in-target.
  moveToPortfolio: (body: MoveLoansToPortfolioRequest) =>
    api.post<MoveLoansResultDto>(`/portfolio-admin/loans/portfolio-moves`, body),
  // Profile tab editing (B6) — loan-terms correction (Loans.Manage, reason
  // required), carried-field edits + financial history (Loans.Update;
  // loan-local — the Deal snapshot is never modified).
  updateTerms: (loanId: string, body: UpdateLoanTermsRequest) =>
    api.put<LoanSummaryDto>(`/portfolio-admin/loans/${loanId}/terms`, body),
  updateIntakeProfile: (loanId: string, body: UpdateLoanIntakeProfileRequest) =>
    api.put<LoanIntakeProfileDto>(
      `/portfolio-admin/loans/${loanId}/intake-profile`,
      body,
    ),
  addFinancialPeriod: (loanId: string, body: UpsertLoanFinancialPeriodRequest) =>
    api.post<FinancialHistoryRow>(
      `/portfolio-admin/loans/${loanId}/financial-history`,
      body,
    ),
  updateFinancialPeriod: (
    loanId: string,
    rowId: string,
    body: UpsertLoanFinancialPeriodRequest,
  ) =>
    api.put<FinancialHistoryRow>(
      `/portfolio-admin/loans/${loanId}/financial-history/${rowId}`,
      body,
    ),
};

// ---- setup checklist -------------------------------------------------

export const loanSetupApi = {
  list: (loanId: string) =>
    api.get<LoanSetupChecklistRowDto[]>(`/portfolio-admin/loans/${loanId}/setup`),
  // Empty body → server derives close date + first payment date from the
  // loan. Idempotent (re-running never duplicates items).
  generate: (loanId: string, body: GenerateSetupChecklistRequest = {}) =>
    api.post<LoanSetupChecklistItemDto[]>(
      `/portfolio-admin/loans/${loanId}/setup/generate`,
      body,
    ),
  complete: (loanId: string, catalogItemId: string, body: CompleteSetupItemRequest) =>
    api.patch<LoanSetupChecklistItemDto>(
      `/portfolio-admin/loans/${loanId}/setup/${catalogItemId}/complete`,
      body,
    ),
  certify: (loanId: string, catalogItemId: string) =>
    api.patch<LoanSetupChecklistItemDto>(
      `/portfolio-admin/loans/${loanId}/setup/${catalogItemId}/certify`,
    ),
  decline: (loanId: string, catalogItemId: string, body: JustificationRequest) =>
    api.patch<LoanSetupChecklistItemDto>(
      `/portfolio-admin/loans/${loanId}/setup/${catalogItemId}/decline`,
      body,
    ),
  waive: (loanId: string, catalogItemId: string, body: JustificationRequest) =>
    api.patch<LoanSetupChecklistItemDto>(
      `/portfolio-admin/loans/${loanId}/setup/${catalogItemId}/waive`,
      body,
    ),
  markComplete: (loanId: string) =>
    api.post<LoanSummaryDto>(`/portfolio-admin/loans/${loanId}/setup/mark-complete`),
};

// ---- portfolios --------------------------------------------------------

export const portfoliosApi = {
  list: () => api.get<PortfolioListItemDto[]>(`/portfolio-admin/portfolios`),
  create: (body: CreatePortfolioRequest) =>
    api.post<PortfolioListItemDto>(`/portfolio-admin/portfolios`, body),
  update: (id: string, body: UpdatePortfolioRequest) =>
    api.patch<PortfolioListItemDto>(`/portfolio-admin/portfolios/${id}`, body),
  dashboard: (id: string) =>
    api.get<PortfolioDashboardDto>(`/portfolio-admin/portfolios/${id}/dashboard`),
};

// ---- assignments (7-pill) --------------------------------------------

export const loanAssignmentsApi = {
  list: (loanId: string) =>
    api.get<LoanAssignmentDto[]>(`/portfolio-admin/loans/${loanId}/assignments`),
  upsert: (loanId: string, roleCode: string, body: AssignLoanRoleRequest) =>
    api.put<LoanAssignmentDto>(
      `/portfolio-admin/loans/${loanId}/assignments/${encodeURIComponent(roleCode)}`,
      body,
    ),
};

// ---- parties (§7.3) --------------------------------------------------

// ---- compliance calendar (§7.6) -------------------------------------

export const loanCalendarApi = {
  list: (
    loanId: string,
    params?: { sourceType?: string; status?: string },
  ) =>
    api.get<ComplianceCalendarItemDto[]>(
      `/portfolio-admin/loans/${loanId}/calendar`,
      params,
    ),
  complete: (itemId: string, body: CompleteCalendarItemRequest) =>
    api.patch<ComplianceCalendarItemDto>(
      `/portfolio-admin/calendar/${itemId}/complete`,
      body,
    ),
  snooze: (itemId: string, body: SnoozeCalendarItemRequest) =>
    api.patch<ComplianceCalendarItemDto>(
      `/portfolio-admin/calendar/${itemId}/snooze`,
      body,
    ),
};

// ---- insurance (§7.4) -----------------------------------------------

export const loanInsuranceApi = {
  list: (loanId: string) =>
    api.get<InsurancePolicyDto[]>(`/portfolio-admin/loans/${loanId}/insurance`),
  create: (loanId: string, body: UpsertInsurancePolicyRequest) =>
    api.post<InsurancePolicyDto>(
      `/portfolio-admin/loans/${loanId}/insurance`,
      body,
    ),
  update: (policyId: string, body: UpsertInsurancePolicyRequest) =>
    api.patch<InsurancePolicyDto>(
      `/portfolio-admin/insurance/${policyId}`,
      body,
    ),
  verifyPhoenix: (policyId: string) =>
    api.post<InsurancePolicyDto>(
      `/portfolio-admin/insurance/${policyId}/verify-phoenix`,
    ),
  verifySeller: (policyId: string) =>
    api.post<InsurancePolicyDto>(
      `/portfolio-admin/insurance/${policyId}/verify-seller`,
    ),
};

// ---- UCC filings + lien searches (§7.5) ------------------------------

export const loanUccApi = {
  listFilings: (loanId: string) =>
    api.get<UccFilingDto[]>(`/portfolio-admin/loans/${loanId}/ucc`),
  fileContinuation: (body: FileUccContinuationRequest) =>
    api.post<UccFilingDto>(`/portfolio-admin/ucc/file-continuation`, body),
  fileTermination: (body: FileUccTerminationRequest) =>
    api.post<UccFilingDto>(`/portfolio-admin/ucc/file-termination`, body),
  fileAmendment: (body: FileUccAmendmentRequest) =>
    api.post<UccFilingDto>(`/portfolio-admin/ucc/file-amendment`, body),
  listSearches: (loanId: string) =>
    api.get<UccLienSearchDto[]>(`/portfolio-admin/ucc/searches`, { loanId }),
  recordSearch: (body: RecordUccLienSearchRequest) =>
    api.post<UccLienSearchDto>(`/portfolio-admin/ucc/searches`, body),
};

// ---- vendors (registry + vendor record foundations) -------------------

/**
 * The single status under which a vendor may be NEWLY assigned to a loan.
 * MIRRORS `VendorStatuses.Assignable` on the server, which is what
 * `LoanPartyAssignmentService.AddPartyAsync` enforces — keep the two in
 * lockstep. If the server's assignable set ever widens past one value, the
 * "Assign vendor" picker needs a multi-value filter (the search endpoint
 * takes a single `status`).
 */
export const VENDOR_ASSIGNABLE_STATUS = "active";

export const vendorsApi = {
  search: (params?: {
    name?: string;
    type?: string;
    status?: string;
    search?: string;
    types?: string[];
    statuses?: string[];
    includeArchived?: boolean;
  }) => api.get<VendorDto[]>(`/portfolio-admin/vendors`, params),
  create: (body: CreateVendorRequest) =>
    api.post<VendorDto>(`/portfolio-admin/vendors`, body),
  get: (vendorId: string) =>
    api.get<VendorDto>(`/portfolio-admin/vendors/${vendorId}`),
  updateProfile: (vendorId: string, body: UpdateVendorProfileRequest) =>
    api.patch<VendorDto>(`/portfolio-admin/vendors/${vendorId}`, body),
  updateStatus: (
    vendorId: string,
    body: { toStatus: string; reason?: string | null },
  ) =>
    api.patch<VendorDto>(
      `/portfolio-admin/vendors/${vendorId}/status`,
      body,
    ),
  assignments: (vendorId: string) =>
    api.get<VendorLoanAssignmentDto[]>(
      `/portfolio-admin/vendors/${vendorId}/assignments`,
    ),
  portalAccount: (vendorId: string) =>
    api.get<VendorPortalAccountDto | null>(
      `/portfolio-admin/vendors/${vendorId}/portal-account`,
    ),
  invitePortal: (vendorId: string) =>
    api.post<VendorPortalAccountDto>(
      `/portfolio-admin/vendors/${vendorId}/portal-invite`,
    ),
  archive: (vendorId: string, body: ArchiveVendorRequest) =>
    api.post<VendorDto>(`/portfolio-admin/vendors/${vendorId}/archive`, body),
  unarchive: (vendorId: string, body: ArchiveVendorRequest) =>
    api.post<VendorDto>(
      `/portfolio-admin/vendors/${vendorId}/unarchive`,
      body,
    ),
  archiveImpact: (vendorId: string) =>
    api.get<VendorArchiveImpactDto>(
      `/portfolio-admin/vendors/${vendorId}/archive-impact`,
    ),
  // ---- SP-4 WS-1 — structured contract terms (effective-dated) ----------
  contractTerms: (vendorId: string) =>
    api.get<VendorContractTermDto[]>(
      `/portfolio-admin/vendors/${vendorId}/contract-terms`,
    ),
  addContractTerm: (vendorId: string, body: CreateVendorContractTermRequest) =>
    api.post<VendorContractTermDto>(
      `/portfolio-admin/vendors/${vendorId}/contract-terms`,
      body,
    ),
  // ---- Step 6 W5 — terms proposed from a countersigned schedule --------
  termProposals: (vendorId: string) =>
    api.get<VendorContractTermProposalDto[]>(
      `/portfolio-admin/vendors/${vendorId}/term-proposals`,
    ),
  confirmTermProposal: (
    vendorId: string,
    proposalId: string,
    body: ConfirmVendorContractTermProposalRequest,
  ) =>
    api.post<VendorContractTermDto>(
      `/portfolio-admin/vendors/${vendorId}/term-proposals/${proposalId}/confirm`,
      body,
    ),
  declineTermProposal: (
    vendorId: string,
    proposalId: string,
    body: DeclineVendorContractTermProposalRequest,
  ) =>
    api.post<VendorContractTermProposalDto>(
      `/portfolio-admin/vendors/${vendorId}/term-proposals/${proposalId}/decline`,
      body,
    ),
};

// ---- vendor billing (VFI invoices) -------------------------------------

export const vendorInvoicesApi = {
  list: (params?: VendorInvoiceListParams) =>
    api.get<VendorInvoiceDto[]>(`/portfolio-admin/vendor-invoices`, params),
  get: (invoiceId: string) =>
    api.get<VendorInvoiceDetailDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}`,
    ),
  issue: (body: IssueVendorInvoiceRequest) =>
    api.post<VendorInvoiceDto>(`/portfolio-admin/vendor-invoices`, body),
  /** The contract check for an invoice that does not exist yet, so the manual
   *  issue form can show the comparison and collect an acknowledgement before
   *  submitting. Gated on VendorBilling.Issue — it exposes contract terms. */
  contractCheck: (vendorId: string, amount: number) =>
    api.get<ContractComparisonDto>(
      `/portfolio-admin/vendor-invoices/contract-check`,
      { vendorId, amount },
    ),
  /** The server-computed VFI rollup for ONE vendor or ONE loan — one set of
   *  definitions for open / in-transit / Paid-YTD / borrower-billed, so the
   *  vendor ledger tab and the loan card cannot disagree. */
  scopedSummary: (params: { vendorId?: string; loanId?: string }) =>
    api.get<VfiScopedSummaryDto>(
      `/portfolio-admin/vendor-invoices/scoped-summary`,
      params,
    ),
  void: (invoiceId: string, body: { reason: string }) =>
    api.post<VendorInvoiceDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}/void`,
      body,
    ),
  recordManualPayment: (
    invoiceId: string,
    body: { receivedAt?: string | null; note?: string | null },
  ) =>
    api.post<VendorInvoiceDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}/manual-payment`,
      body,
    ),
  correctStatus: (invoiceId: string, body: { toStatus: string; reason: string }) =>
    api.post<VendorInvoiceDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}/correct-status`,
      body,
    ),
  /** Webhook-outage escape hatch: re-fetches the Stripe intent and applies
   *  the same status derivation the webhook uses. */
  refreshPayment: (invoiceId: string) =>
    api.post<VendorInvoiceDetailDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}/refresh-payment`,
    ),
  dashboard: () =>
    api.get<VfiDashboardDto>(`/portfolio-admin/vendor-invoices/dashboard`),
  // ---- Vendor Program v2 PR 3 — VFI Accounting --------------------------
  /** Draft → Issued for one draft (the extracted v1 issue block). SP-4:
   *  when the contract check reports a variance, the server requires
   *  `varianceAckReason` (400 without it). */
  issueDraft: (invoiceId: string, varianceAckReason?: string | null) =>
    api.post<VendorInvoiceDto>(
      `/portfolio-admin/vendor-invoices/${invoiceId}/issue`,
      { varianceAckReason: varianceAckReason ?? null },
    ),
  /** Batch send: per-item isolation — non-Draft ids fail per-item. */
  batchIssue: (body: BatchIssueRequest) =>
    api.post<BatchIssueResultDto>(
      `/portfolio-admin/vendor-invoices/batch-issue`,
      body,
    ),
  pastIncome: () =>
    api.get<VfiPastIncomeRowDto[]>(
      `/portfolio-admin/vendor-invoices/accounting/past-income`,
    ),
  receivablesAging: () =>
    api.get<VfiAgingRowDto[]>(
      `/portfolio-admin/vendor-invoices/accounting/receivables-aging`,
    ),
  inTransit: () =>
    api.get<VfiAgingRowDto[]>(
      `/portfolio-admin/vendor-invoices/accounting/in-transit`,
    ),
  outstanding: () =>
    api.get<VfiAgingRowDto[]>(
      `/portfolio-admin/vendor-invoices/accounting/outstanding`,
    ),
  ratingRollup: () =>
    api.get<VfiRatingRollupDto>(
      `/portfolio-admin/vendor-invoices/accounting/rating-rollup`,
    ),
};

// ---- required-vendor catalog (feeds the "Assign vendor" role dropdown) --

export const requiredVendorsApi = {
  catalog: () =>
    api.get<RequiredVendorCatalogItemDto[]>(
      `/portfolio-admin/required-vendor-catalog`,
    ),
};

export const loanPartiesApi = {
  list: (loanId: string, includeInactive = false) =>
    api.get<LoanPartyAssignmentDto[]>(`/portfolio-admin/loans/${loanId}/parties`, {
      includeInactive,
    }),
  add: (loanId: string, body: AddLoanPartyRequest) =>
    api.post<LoanPartyAssignmentDto>(
      `/portfolio-admin/loans/${loanId}/parties`,
      body,
    ),
  update: (partyAssignmentId: string, body: UpdateLoanPartyRequest) =>
    api.patch<LoanPartyAssignmentDto>(
      `/portfolio-admin/parties/${partyAssignmentId}`,
      body,
    ),
  inactivate: (partyAssignmentId: string) =>
    api.delete<LoanPartyAssignmentDto>(`/portfolio-admin/parties/${partyAssignmentId}`),
  seedFromDeal: (loanId: string) =>
    api.post<SeedPartiesFromDealResultDto>(
      `/portfolio-admin/loans/${loanId}/parties/seed-from-deal`,
    ),
};

// ---- Vendor Connection v1 ("Make a Connection") ----------------------
// Connect/disconnect gate Vendors.Manage server-side; list is Vendors.Read.

export const vendorConnectionsApi = {
  listForLoan: (loanId: string) =>
    api.get<VendorConnectionDto[]>(
      `/portfolio-admin/loans/${loanId}/vendor-connections`,
    ),
  connect: (partyAssignmentId: string) =>
    api.post<VendorConnectionDto>(`/portfolio-admin/vendor-connections`, {
      partyAssignmentId,
    }),
  disconnect: (connectionId: string, reason?: string) =>
    api.post<VendorConnectionDto>(
      `/portfolio-admin/vendor-connections/${connectionId}/disconnect`,
      { reason: reason ?? null },
    ),
  updateEngagementStatus: (connectionId: string, status: string) =>
    api.patch<VendorConnectionDto>(
      `/portfolio-admin/vendor-connections/${connectionId}/engagement-status`,
      { status },
    ),
};

// ---- Setup catalog admin (Loan record foundations) -------------------

export const loanSetupCatalogApi = {
  list: (includeInactive = false) =>
    api.get<LoanSetupCatalogItemDto[]>(`/portfolio-admin/setup-catalog`, {
      includeInactive,
    }),
  create: (body: CreateSetupCatalogItemRequest) =>
    api.post<LoanSetupCatalogItemDto>(`/portfolio-admin/setup-catalog`, body),
  update: (id: string, body: UpdateSetupCatalogItemRequest) =>
    api.patch<LoanSetupCatalogItemDto>(`/portfolio-admin/setup-catalog/${id}`, body),
};

// ---- Loan profile admin fields (Loan record foundations) -------------

export const loanProfileFieldsApi = {
  listDefinitions: (includeInactive = false) =>
    api.get<LoanProfileFieldDefinitionDto[]>(`/portfolio-admin/profile-fields`, {
      includeInactive,
    }),
  createDefinition: (body: CreateLoanProfileFieldDefinitionRequest) =>
    api.post<LoanProfileFieldDefinitionDto>(`/portfolio-admin/profile-fields`, body),
  updateDefinition: (id: string, body: UpdateLoanProfileFieldDefinitionRequest) =>
    api.patch<LoanProfileFieldDefinitionDto>(
      `/portfolio-admin/profile-fields/${id}`,
      body,
    ),
  values: (loanId: string) =>
    api.get<LoanProfileFieldValueDto[]>(
      `/portfolio-admin/loans/${loanId}/profile-fields`,
    ),
  setValue: (loanId: string, definitionId: string, body: SetLoanProfileFieldValueRequest) =>
    api.put<LoanProfileFieldValueDto>(
      `/portfolio-admin/loans/${loanId}/profile-fields/${definitionId}`,
      body,
    ),
};
